import { useEffect, useRef } from "react";

export type UseYearSelectorKeysParams = {
  columns: number;
  scrollToYear: (year: number, { focus }: { focus?: boolean }) => void;
  initialFocus: number;
  onSelect: (year: number | null) => void;
  onConfirm: () => void;
  min: number;
  max: number;
  disabled: boolean;
  pseudoFocused: number;
  setPseudoFocused: (year: number) => void;
  required: boolean;
};

export type UseYearSelectorKeysReturn = {
  listRef: React.RefObject<HTMLDivElement>;
};

export const useYearSelectorKeys = ({
  columns,
  scrollToYear,
  initialFocus,
  onSelect,
  onConfirm,
  pseudoFocused,
  setPseudoFocused,
  min,
  max,
  disabled,
  required,
}: UseYearSelectorKeysParams): UseYearSelectorKeysReturn => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (pseudoFocused === null) return;
      if (
        [
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "PageUp",
          "PageDown",
          "Enter",
          " ",
          "Backspace",
          "Delete",
        ].includes(e.key)
      ) {
        e.preventDefault();
        if (
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "ArrowUp" ||
          e.key === "ArrowDown"
        ) {
          const newYear =
            e.key === "ArrowLeft" || e.key === "ArrowUp"
              ? pseudoFocused - 1
              : pseudoFocused + 1;
          const clampedYear = Math.min(Math.max(newYear, min), max);
          scrollToYear(newYear, { focus: true });
          setPseudoFocused(newYear);
          if (!disabled) {
            onSelect(clampedYear);
          }
          return;
        }
        if (!required && (e.key === "Backspace" || e.key === "Delete")) {
          onSelect(null);
          return;
        }
        if (e.key === "PageUp" || e.key === "PageDown") {
          const newYear = e.key === "PageUp" ? min : max;
          scrollToYear(newYear, { focus: true });
          setPseudoFocused(newYear);
          if (!disabled) {
            onSelect(newYear);
          }
          return;
        }
        if (e.key === "Home") {
          scrollToYear(initialFocus, { focus: true });
          setPseudoFocused(initialFocus);
          if (!disabled) {
            onSelect(initialFocus);
          }
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          if (!disabled) {
            onConfirm();
          }
          return;
        }
      }
    };
    list?.addEventListener("keydown", handleKeyDown);
    return () => {
      list?.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    pseudoFocused,
    columns,
    scrollToYear,
    onSelect,
    min,
    max,
    initialFocus,
    disabled,
    setPseudoFocused,
    required,
    onConfirm,
  ]);

  return { listRef };
};
